import { type Community, type CommunityMembership, type Role, type User } from '@/types';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  NOTIFICATION_FIELD_MAPPINGS,
  NOTIFICATION_PREFERENCE_KEYS,
} from '@/data/notificationPreferences';
import { normalizeEmail } from '@/utils/validations';

type PrimitiveUser = Partial<User> & {
  created_at?: string | Date | null;
  community_id?: string | number | null;
  last_name?: string | null;
  community?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeRole = (value: unknown, fallback: Role = 'neighbor'): Role => {
  if (typeof value !== 'string') return fallback;

  const trimmed = value.trim().toLowerCase();
  const allowedRoles: Role[] = [
    'admin',
    'manager',
    'president',
    'vocal',
    'neighbor',
    'renter',
    'admin_general',
    'owner',
    'tenant',
    'super_admin',
    'guest',
    'admin_delegate',
  ];

  return allowedRoles.includes(trimmed as Role) ? (trimmed as Role) : fallback;
};

const toDateOrUndefined = (value?: string | Date | null) => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const toOptionalNullableString = (value: unknown): string | null | undefined => {
  if (typeof value === 'string') return value;
  return value === null ? null : undefined;
};

export const toFrontUser = <T extends PrimitiveUser>(user: T): User => {
  const normalizedRecord: Record<string, unknown> = { ...user };
  const userRecord: Record<string, unknown> = isRecord(user) ? user : {};

  const lastName = user.lastName ?? user.last_name ?? undefined;
  const createdAt = toDateOrUndefined(user.createdAt ?? user.created_at ?? undefined);

  const emailValue = user.email;
  const normalizedEmail = typeof emailValue === 'string' ? normalizeEmail(emailValue) : emailValue;

  normalizedRecord.email = normalizedEmail ?? '';
  normalizedRecord.last_name = lastName;
  normalizedRecord.lastName = lastName;
  normalizedRecord.createdAt = createdAt;
  normalizedRecord.communityId = null;


  const rawMemberships = userRecord.user_roles_by_community;
  const normalizeCommunityId = (value: unknown): string | null => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return null;
  };
  let communityMemberships: CommunityMembership[] | undefined;
  let normalizedRole: Role = normalizeRole(user.role);
  let normalizedBlock =
    typeof user.block === 'string' || user.block === null ? user.block : undefined;
  let normalizedFloor =
    typeof user.floor === 'string' || user.floor === null ? user.floor : undefined;
  let normalizedLetter =
    typeof user.letter === 'string' || user.letter === null ? user.letter : undefined;

  if (Array.isArray(rawMemberships)) {
    const memberships = rawMemberships
      .map((item) => (isRecord(item) ? item : null))
      .filter((item): item is Record<string, unknown> => Boolean(item));

    const membershipCommunityIds = memberships
      .map((item) => normalizeCommunityId(item.community_id))
      .filter((id): id is string => Boolean(id));

    if (membershipCommunityIds.length > 0) {
      normalizedRecord.communityIds = Array.from(new Set(membershipCommunityIds));
    }

    communityMemberships = memberships
      .map((item) => {
        const communityId = normalizeCommunityId(item.community_id);
        if (!communityId) return null;
        const rawQuota = item.participation_quota;
        const participationQuota = typeof rawQuota === 'number' && Number.isFinite(rawQuota)
          ? rawQuota
          : typeof rawQuota === 'string' && rawQuota.trim() !== '' && !isNaN(Number(rawQuota))
            ? Number(rawQuota)
            : null;
        return {
          communityId,
          role: typeof item.role === 'string' ? normalizeRole(item.role) : undefined,
          block: typeof item.block === 'string' ? item.block : item.block === null ? null : undefined,
          floor: typeof item.floor === 'string' ? item.floor : item.floor === null ? null : undefined,
          letter: typeof item.letter === 'string' ? item.letter : item.letter === null ? null : undefined,
          isDefault: item.is_default === true,
          isDefaulter: item.is_defaulter === true,
          participationQuota: participationQuota ?? undefined,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

    normalizedRecord.communityMemberships = communityMemberships;

    const defaultMembership = memberships.find((item) => item.is_default === true) ?? memberships[0];
    if (defaultMembership) {
      const defaultCommunityId = normalizeCommunityId(defaultMembership.community_id);
      if (defaultCommunityId) {
        normalizedRecord.communityId = defaultCommunityId;
      }
      normalizedRole = normalizeRole(defaultMembership.role, normalizedRole);

      const membershipBlock = toOptionalNullableString(defaultMembership.block);
      if (membershipBlock !== undefined) {
        normalizedBlock = membershipBlock;
        normalizedRecord.block = membershipBlock;
      }
      const membershipFloor = toOptionalNullableString(defaultMembership.floor);
      if (membershipFloor !== undefined) {
        normalizedFloor = membershipFloor;
        normalizedRecord.floor = membershipFloor;
      }
      const membershipLetter = toOptionalNullableString(defaultMembership.letter);
      if (membershipLetter !== undefined) {
        normalizedLetter = membershipLetter;
        normalizedRecord.letter = membershipLetter;
      }
      normalizedRecord.isDefaulter = defaultMembership.is_defaulter === true;
      const rawDefaultQuota = defaultMembership.participation_quota;
      const defaultQuota = typeof rawDefaultQuota === 'number' && Number.isFinite(rawDefaultQuota)
        ? rawDefaultQuota
        : typeof rawDefaultQuota === 'string' && rawDefaultQuota.trim() !== '' && !isNaN(Number(rawDefaultQuota))
          ? Number(rawDefaultQuota)
          : null;
      normalizedRecord.participationQuota = defaultQuota;
    }
  }

  delete normalizedRecord.user_roles_by_community;
  const rawAvatar = userRecord.avatar;
  if (typeof rawAvatar === 'string' && rawAvatar.trim().length > 0) {
    const trimmed = rawAvatar.trim();
    normalizedRecord.avatarPath = trimmed;
    normalizedRecord.avatar = trimmed;
  } else {
    normalizedRecord.avatarPath = null;
    if ('avatar' in normalizedRecord) {
      delete normalizedRecord.avatar;
    }
  }

  const housingFields: Array<'block' | 'floor' | 'letter'> = ['block', 'floor', 'letter'];
  housingFields.forEach((field) => {
    if (!(field in normalizedRecord)) {
      normalizedRecord[field] = null;
    }
  });

  const notiLast = toNullableNumber(user.notiLast ?? userRecord.noti_last);
  normalizedRecord.notiLast = notiLast;
  normalizedRecord.noti_last = notiLast;

  delete normalizedRecord.created_at;
  delete normalizedRecord.community_id;

  const notificationDefaults = DEFAULT_NOTIFICATION_PREFERENCES;
  for (const key of NOTIFICATION_PREFERENCE_KEYS) {
    const camelValue = userRecord[key];
    let resolved = typeof camelValue === 'boolean' ? camelValue : undefined;
    if (resolved === undefined) {
      const mappedKey = NOTIFICATION_FIELD_MAPPINGS[key];
      const snakeValue = userRecord[mappedKey];
      resolved = typeof snakeValue === 'boolean' ? snakeValue : notificationDefaults[key];
    }
    normalizedRecord[key] = resolved;
    normalizedRecord[NOTIFICATION_FIELD_MAPPINGS[key]] = resolved;
  }

  if ('community' in normalizedRecord) {
    const rawCommunity = normalizedRecord.community;
    if (rawCommunity) {
      const communities = Array.isArray(rawCommunity) ? rawCommunity : [rawCommunity];
      normalizedRecord.communities = communities.filter(
        (community): community is Community => isRecord(community) && typeof community.id === 'string',
      );
    }
    delete normalizedRecord.community;
  }

  const communities = Array.isArray(normalizedRecord.communities)
    ? normalizedRecord.communities.filter((community): community is Community => isRecord(community) && typeof community.id === 'string')
    : undefined;
  const communityIdValue = toOptionalNullableString(normalizedRecord.communityId);
  const communityId: string | null = communityIdValue ?? null;
  const communityIds = Array.isArray(normalizedRecord.communityIds)
    ? normalizedRecord.communityIds.filter((communityId): communityId is string => typeof communityId === 'string')
    : undefined;

  const deletionRequestedAt = toDateOrUndefined(
    user.deletionRequestedAt ?? (userRecord.deletion_requested_at as string | Date | null | undefined)
  );
  const deletionEffectiveAt = toDateOrUndefined(
    user.deletionEffectiveAt ?? (userRecord.deletion_effective_at as string | Date | null | undefined)
  );
  const isBlocked = user.isBlocked === true || userRecord.is_blocked === true;
  const blockedReason = toOptionalNullableString(user.blockedReason ?? userRecord.blocked_reason) ?? null;

  const result: User = {
    id: typeof user.id === 'string' ? user.id : '',
    name: typeof user.name === 'string' ? user.name : '',
    email: typeof normalizedEmail === 'string' ? normalizedEmail : '',
    role: normalizedRole,
    status: user.status,
    createdAt,
    last_name: lastName,
    lastName,
    avatarPath: typeof normalizedRecord.avatarPath === 'string' ? normalizedRecord.avatarPath : null,
    avatar: typeof normalizedRecord.avatar === 'string' ? normalizedRecord.avatar : undefined,
    phone: typeof user.phone === 'string' || user.phone === null ? user.phone : undefined,
    address: typeof user.address === 'string' ? user.address : undefined,
    block: normalizedBlock ?? null,
    floor: normalizedFloor ?? null,
    letter: normalizedLetter ?? null,
    secret: typeof user.secret === 'string' ? user.secret : undefined,
    deletionRequestedAt,
    deletionEffectiveAt,
    isBlocked,
    blockedReason,
    communities,
    communityId,
    communityIds,
    communityMemberships,
    notiEmail: normalizedRecord.notiEmail === true ? true : normalizedRecord.notiEmail === false ? false : undefined,
    notiPush: normalizedRecord.notiPush === true ? true : normalizedRecord.notiPush === false ? false : undefined,
    notiEvents: normalizedRecord.notiEvents === true ? true : normalizedRecord.notiEvents === false ? false : undefined,
    notiPayments: normalizedRecord.notiPayments === true ? true : normalizedRecord.notiPayments === false ? false : undefined,
    notiIncidents: normalizedRecord.notiIncidents === true ? true : normalizedRecord.notiIncidents === false ? false : undefined,
    notiMeetings: normalizedRecord.notiMeetings === true ? true : normalizedRecord.notiMeetings === false ? false : undefined,
    notiAgreements: normalizedRecord.notiAgreements === true ? true : normalizedRecord.notiAgreements === false ? false : undefined,
    notiNotices: normalizedRecord.notiNotices === true ? true : normalizedRecord.notiNotices === false ? false : undefined,
    notiPolls: normalizedRecord.notiPolls === true ? true : normalizedRecord.notiPolls === false ? false : undefined,
    notiProposals: normalizedRecord.notiProposals === true ? true : normalizedRecord.notiProposals === false ? false : undefined,
    notiDocuments: normalizedRecord.notiDocuments === true ? true : normalizedRecord.notiDocuments === false ? false : undefined,
    notiLast,
    noti_email: normalizedRecord.noti_email === true ? true : normalizedRecord.noti_email === false ? false : undefined,
    noti_push: normalizedRecord.noti_push === true ? true : normalizedRecord.noti_push === false ? false : undefined,
    noti_events: normalizedRecord.noti_events === true ? true : normalizedRecord.noti_events === false ? false : undefined,
    noti_payments: normalizedRecord.noti_payments === true ? true : normalizedRecord.noti_payments === false ? false : undefined,
    noti_incidents: normalizedRecord.noti_incidents === true ? true : normalizedRecord.noti_incidents === false ? false : undefined,
    noti_meetings: normalizedRecord.noti_meetings === true ? true : normalizedRecord.noti_meetings === false ? false : undefined,
    noti_agreements: normalizedRecord.noti_agreements === true ? true : normalizedRecord.noti_agreements === false ? false : undefined,
    noti_notices: normalizedRecord.noti_notices === true ? true : normalizedRecord.noti_notices === false ? false : undefined,
    noti_polls: normalizedRecord.noti_polls === true ? true : normalizedRecord.noti_polls === false ? false : undefined,
    noti_proposals: normalizedRecord.noti_proposals === true ? true : normalizedRecord.noti_proposals === false ? false : undefined,
    noti_documents: normalizedRecord.noti_documents === true ? true : normalizedRecord.noti_documents === false ? false : undefined,
    noti_last: notiLast,
  };

  return result;
};

export const toApiUser = (user: Partial<User>): Record<string, unknown> => {
  const payload: Record<string, unknown> = { ...user };

  if ('communities' in payload) {
    delete payload.communities;
  }
  if ('communityIds' in payload) {
    delete payload.communityIds;
  }
  if ('communityMemberships' in payload) {
    delete payload.communityMemberships;
  }

  if ('password' in payload) {
    const value = payload.password as unknown;
    if (value === undefined || value === null || value === '') {
      delete payload.password;
    }
  }

  if ('communityId' in payload) {
    delete payload.communityId;
  }

  if ('lastName' in payload) {
    payload.last_name = payload.lastName;
    delete payload.lastName;
  }

  if ('createdAt' in payload) {
    const value = payload.createdAt;
    payload.created_at = value instanceof Date ? value.toISOString() : value;
    delete payload.createdAt;
  }

  if ('notiLast' in payload) {
    payload.noti_last = payload.notiLast;
    delete payload.notiLast;
  }

  if ('deletionRequestedAt' in payload) {
    const value = payload.deletionRequestedAt;
    payload.deletion_requested_at = value instanceof Date ? value.toISOString() : value;
    delete payload.deletionRequestedAt;
  }
  if ('deletionEffectiveAt' in payload) {
    const value = payload.deletionEffectiveAt;
    payload.deletion_effective_at = value instanceof Date ? value.toISOString() : value;
    delete payload.deletionEffectiveAt;
  }
  if ('isBlocked' in payload) {
    payload.is_blocked = payload.isBlocked;
    delete payload.isBlocked;
  }
  if ('blockedReason' in payload) {
    payload.blocked_reason = payload.blockedReason;
    delete payload.blockedReason;
  }

  if ('role' in payload) {
    delete payload.role;
  }
  if ('block' in payload) {
    delete payload.block;
  }
  if ('floor' in payload) {
    delete payload.floor;
  }
  if ('letter' in payload) {
    delete payload.letter;
  }

  for (const key of NOTIFICATION_PREFERENCE_KEYS) {
    if (key in payload) {
      const mappedKey = NOTIFICATION_FIELD_MAPPINGS[key];
      payload[mappedKey] = payload[key];
      delete payload[key];
    }
  }

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined) {
      delete payload[key];
    }
  });

  return payload;
};
